// useCurrentUser — central source of truth for the logged-in user's
// profile (id + role + enabled_modules). Pages can derive permissions
// from this without re-querying the users table everywhere.
//
// The query key is `['me']` to share cache with existing pages that
// already use that key (Users, Financial, UploadCostings).
//
// Auth call: getSession() not getUser(). getUser() acquires the Supabase
// JS Web-Locks auth lock and makes a network round-trip to /auth/user
// to revalidate the JWT. Multiple tabs / components racing it (we have
// useCurrentUser in nearly every page + an ambient TanStack-Query
// retry) can have the lock stolen by a token-refresh, surfacing as
//   Error: Lock "lock:sb-...-auth-token" was released because another
//          request stole it
// in production (Sentry, 2026-05-10). getSession() reads the cached
// session from localStorage with no lock and no network — safe for the
// "I just need my user_id to load my profile" path. The same swap
// applies to error-reporter + notifications mutationFns.

import { useEffect } from 'react'
import { POLL } from '@/lib/pollTiers'
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { subscribeToTables, removeRealtime } from '@/lib/realtime'
import { decodeStoredClaims, decodeJwtPayload, LAST_ME_KEY } from '@/lib/offlineSession'
import type { UserRole } from '@/types/database'

// Durable snapshot of the resolved profile (incl. the per-tenant effective
// role + enabled_modules), kept in a dedicated long-lived localStorage key so a
// scout offline > the token lifetime still resolves their identity + permissions
// (the route gate RequireModule reads this). Without it, offline the users
// select + current_tenant_role RPC fail → the query throws → `me` is undefined →
// every gFarm route bounces, and the role would otherwise collapse to the global
// users.role. Keyed on the user id so a different login never restores a stale
// snapshot. See useActiveTenant's kf49.lastActiveTenant for the sibling pattern.
function readLastMe(): CurrentUser | null {
  try {
    const raw = localStorage.getItem(LAST_ME_KEY)
    return raw ? (JSON.parse(raw) as CurrentUser) : null
  } catch { return null }
}
function writeLastMe(me: CurrentUser): void {
  try { localStorage.setItem(LAST_ME_KEY, JSON.stringify(me)) } catch { /* quota/unavailable — best effort */ }
}

// Module-level singleton subscription. Previously each mounted hook
// opened its own realtime channel (random uuid suffix) so a tab with
// 8 components calling useCurrentUser held 8 sockets multiplexed
// against the same row — wasteful, plus the realtime layer eventually
// hit per-client channel limits in long-lived sessions. Now the first
// mount per session installs ONE channel keyed on the user id; later
// mounts reuse it. The channel is rebuilt only when the userId
// changes (e.g. after sign-out + sign-in). qc registration is also
// captured once so any QueryClient instance can invalidate ['me']
// when the row changes.
let meChannelUserId: string | null = null
let meChannel: ReturnType<typeof supabase.channel> | null = null
const meSubscribers = new Set<QueryClient>()

// One-time-per-boot "I'm active" stamp. last_login_at is otherwise written
// only by the password-login path (record_login_attempt, mig 681), so a user
// who accepted an invite and set a password never gets it set — leaving the
// members page stuck on "Pending — invite sent" (see touch_last_login, mig
// 20260713190000). Fire it once per userId per page load, and only on a LIVE
// session (never the offline snapshot path — the RPC needs a real token). The
// RPC is a no-op once the column is set, so this is fire-and-forget: failure
// (offline, weak link) just means the badge clears on a later boot.
const touchedLogin = new Set<string>()
function touchLastLoginOnce(userId: string): void {
  if (touchedLogin.has(userId)) return
  touchedLogin.add(userId)
  void supabase.rpc('touch_last_login').then(({ error }) => {
    // Roll back the guard on failure so a later boot retries.
    if (error) touchedLogin.delete(userId)
  }, () => { touchedLogin.delete(userId) })
}

function ensureMeChannel(userId: string, qc: QueryClient): void {
  meSubscribers.add(qc)

  // Same user as the current channel — keep it ONLY while it's actually
  // alive. A server-initiated close (e.g. token expiry during device sleep)
  // detaches the channel from the realtime client permanently while this
  // module still holds the dead object; the old truthy check then pinned
  // 'me' liveness — and, as the socket's keep-alive channel, the whole
  // realtime connection — to a corpse for the rest of the session. A
  // closed/errored channel falls through and is rebuilt fresh.
  if (meChannelUserId === userId && meChannel
      && ['joined', 'joining'].includes(meChannel.state as string)) return

  // Either no channel yet, or the user changed (signed out + back in
  // as someone else). Tear down the old one before installing the new.
  if (meChannel) {
    removeRealtime(meChannel)
    meChannel = null
  }

  meChannelUserId = userId
  const fanout = () => {
    for (const client of meSubscribers) {
      client.invalidateQueries({ queryKey: ['me'] })
    }
  }
  // Per-tenant role lives on user_tenants; the overlay below merges it into
  // me.role, so changes there must also invalidate ['me'].
  meChannel = subscribeToTables(`me:${userId}`, [
    { table: 'users',        event: 'UPDATE', filter: `id=eq.${userId}` },
    { table: 'user_tenants', event: 'UPDATE', filter: `user_id=eq.${userId}` },
  ], fanout)
}

export interface CurrentUser {
  id:                          string
  name:                        string
  role:                        UserRole
  enabled_modules:             string[]
  is_active:                   boolean
  locked_until:                string | null
  can_override_delivery_date:  boolean
  can_manage_budget:           boolean
  // POPIA consents (mig 505). Shape: { "<policy-version>": { accepted_at, ... } }
  // ConsentGate reads this to decide whether to interrupt the user.
  consents:                    Record<string, unknown>
  /** The ACTIVE tenant's own role for this user — what RLS's
   *  current_tenant_role() returns — with none of `role`'s fallbacks: not the
   *  sticky SUPER_ADMIN, not the global users.role. null = no membership role
   *  here (a support view mints none). Undefined only on an offline snapshot
   *  written before this field existed. Gate a write on this, not `role`, when
   *  its policy names tenant roles. */
  tenant_role?:                UserRole | null
}

export function useCurrentUser() {
  const qc = useQueryClient()
  const query = useQuery<CurrentUser | null>({
    queryKey: ['me'],
    staleTime: 60_000,
    // Council T3.10. force-sign-out-user invalidates refresh tokens but the
    // access token stays valid up to ~1h, and is_active/locked_until are
    // only re-read when this query goes stale. A poll + focus-refetch means
    // a deactivated or force-signed-out user's RequireActiveUser gate (in
    // App.tsx) fires within ~2 min / on next tab focus instead of waiting
    // for the access token to age out. Doesn't replace server-side
    // revocation — narrows the practical exposure window.
    refetchInterval: POLL.relaxed,
    refetchOnWindowFocus: true,
    // Backstop for the lock-stolen race that getSession should already
    // avoid — if some adjacent code path (auth refresh on app focus,
    // multi-tab sign-in) still races us, retry once before bubbling.
    retry: (failureCount, error) => {
      const msg = (error as Error)?.message ?? ''
      if (failureCount < 1 && /lock.*was released because another request stole it/i.test(msg)) {
        return true
      }
      return failureCount < 1
    },
    queryFn: async () => {
      const { data: sess } = await supabase.auth.getSession()
      // Offline + expired token: getSession() returns null. Recover the user id
      // from the stored token so the profile still resolves from the snapshot
      // below rather than collapsing to a null (signed-out) me.
      const userId = sess.session?.user?.id ?? decodeStoredClaims()?.sub ?? undefined
      if (!userId) return null
      try {
        // Try with enabled_modules first. If migration 084 hasn't been
        // applied yet, the column won't exist and PostgREST returns 42703.
        // Fall back to the pre-084 shape so the app still works (admins
        // see everything via the role bypass; non-admins get role defaults).
        // Fetch the per-tenant role in parallel so we can overlay it
        // (mig 200 helper). Multi-tenant users have distinct roles per
        // tenant and we want module gates + isManagerOrAbove() to reflect
        // their role in the ACTIVE tenant context, not their global
        // users.role. SUPER_ADMIN is the only platform-wide power tier;
        // we keep it sticky so destructive operations still work even
        // when a SUPER_ADMIN is a plain ADMIN at the tenant they're in.
        const [primary, tenantRoleRes, budgetRes] = await Promise.all([
          supabase.from('users')
            .select('id, name, role, enabled_modules, is_active, locked_until, can_override_delivery_date, can_manage_budget, consents')
            .eq('id', userId)
            .maybeSingle(),
          supabase.rpc('current_tenant_role'),
          // FM09 RQ-2 (mig 20261084000000): budget access is a flag on the membership of
          // the ACTIVE business, so it is asked of the same helper the Budget policies
          // use. Before that migration the helper still reads users.can_manage_budget,
          // so the answer is right either way.
          supabase.rpc('current_user_can_manage_budget'),
        ])
        let row: {
          id: string; name: string; role: UserRole;
          enabled_modules?: string[] | null;
          is_active?: boolean | null;
          locked_until?: string | null;
          can_override_delivery_date?: boolean | null;
          can_manage_budget?: boolean | null;
          consents?: Record<string, unknown> | null;
        } | null = primary.data
        if (primary.error) {
          if (/column.*(enabled_modules|locked_until|can_override_delivery_date|can_manage_budget|consents)/i.test(primary.error.message)) {
            // Earlier migration not applied yet — fall back to the bare-minimum
            // select so the app still works. Optional flags default to safe values.
            const fallback = await supabase.from('users')
              .select('id, name, role, is_active')
              .eq('id', userId)
              .maybeSingle()
            if (fallback.error) throw fallback.error
            row = fallback.data
          } else {
            throw primary.error
          }
        }
        if (!row) return null
        const globalRole = row.role
        // mig 710: when the auth hook emits tenant_role, the per-tenant role
        // rides in the JWT — prefer it over the current_tenant_role RPC (one
        // fewer round-trip, and it resolves offline straight from the live or
        // stored token). Fall back to the RPC for tokens minted before the claim.
        const liveTok    = sess.session?.access_token
        const claims     = (liveTok ? decodeJwtPayload(liveTok) : null) ?? decodeStoredClaims()?.raw ?? null
        const claimRole  = typeof claims?.tenant_role === 'string' ? claims.tenant_role : null
        const tenantRole = (claimRole ?? tenantRoleRes.data ?? null) as UserRole | null
        // SUPER_ADMIN stays sticky (platform tier). Otherwise prefer the
        // per-tenant role when present; fall back to global for tenant-
        // less contexts (e.g. platform admin acting cross-tenant).
        const effectiveRole: UserRole =
          globalRole === 'SUPER_ADMIN' ? 'SUPER_ADMIN' : (tenantRole ?? globalRole)
        const result = {
          ...row,
          role:                       effectiveRole,
          tenant_role:                tenantRole,
          enabled_modules:            row.enabled_modules ?? [],
          is_active:                  row.is_active ?? true,
          locked_until:               row.locked_until ?? null,
          can_override_delivery_date: row.can_override_delivery_date ?? false,
          can_manage_budget:          budgetRes.error || budgetRes.data == null ? (row.can_manage_budget ?? false) : budgetRes.data === true,
          consents:                   row.consents ?? {},
        } as CurrentUser
        // Snapshot the resolved profile for offline cold boots / token expiry.
        writeLastMe(result)
        // Clear the stale "Pending — invite sent" badge for invite-accept users
        // whose last_login_at was never stamped. Only on a live session (we have
        // a real token here) and at most once per boot.
        if (sess.session) touchLastLoginOnce(userId)
        return result
      } catch (e) {
        // The users select / current_tenant_role rpc failed (supabase-js resolves
        // a failed fetch as { error }, re-raised by the throws above). Reuse the
        // last-known profile for THIS user — incl. the per-tenant effective role +
        // enabled_modules — rather than letting the ['me'] query throw, which
        // would strip route access (RequireModule) and collapse the role.
        //
        // Do NOT gate this on navigator.onLine (the old isOffline() check): it
        // reports `true` on a weak/EDGE link or captive portal — the farm-field
        // case — so a real fetch failure there fell through to `throw e` and the
        // gFarm routes bounced even though the App.tsx auth gate had let the user
        // in. Symmetric with useActiveTenant, which keys off the actual rpc error,
        // not the onLine flag. This doesn't mask online deactivation / force-
        // sign-out / role-revocation: those arrive via the SUCCESS path (a read of
        // is_active / the tenant role value), or clear the stored token (→ me
        // null → Login) — none of them flow through this catch. The 120s refetch
        // heals the profile the moment connectivity returns.
        const last = readLastMe()
        if (last && last.id === userId) return last
        throw e
      }
    },
  })

  // Realtime — when an admin flips a flag on the caller's own users row
  // (role, enabled_modules, is_active, can_override_delivery_date, …),
  // invalidate ['me'] so the change propagates within a second. The
  // subscription is a module-level singleton (see ensureMeChannel
  // above); every hook mount registers its QueryClient with that
  // singleton and the first one installs the channel.
  const userId = query.data?.id ?? null
  useEffect(() => {
    if (!userId) return
    ensureMeChannel(userId, qc)
  }, [userId, qc])

  return query
}
